import { useLazyQuery } from '@apollo/client';
import { Controller } from 'react-hook-form';
import { useContext, useEffect, useState } from 'react';
import {
  Button,
  Col,
  FormFeedback,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  Label,
} from 'reactstrap';
import { FETCH_CONTACTS_FOR_TIMECARD } from '../gql';
import InputField from '../../../../components/layout/InputField';
import CreateContactModal from '../../../company/company-components/CreateContactModal';
import moment from 'moment';
import {
  commaRemover,
  localStringMaker,
  moneyFieldHandler,
} from '../../../../utils/helper';
import { AppContext } from '../../../../context';
import { customSelectDropDown, customSelectStyleNew } from '../../../../components/styled/customSelect';
import AsyncContactController from '../../../../components/AsyncContactController';
import Select from 'react-select';

// Single-page cap for the company-contact selects (timecard approver, billing
// contact, ...). These selects filter CLIENT-side over one fetched page, so
// this must exceed any real company's contact count — kan-81 raised it from 30
// after a 62-contact company silently lost half its list. The overflow guard
// below warns if a company ever tops this.
const CONTACT_FETCH_LIMIT = 500;

export default function ContractPlacementTypeBase({
  companyId,
  companyName,
  control,
  readonly,
  startDateString,
  setEndingDate,
  register,
  watch,
  setAssignedDuration,
  setValue,
  errors,
  setError,
  disableAllFields,
  setEditModal
}: any) {
  const { theme } = useContext(AppContext);
  const [createContactModal, setCreateContactModal] = useState<boolean>(false);

  const [_getCompanyContacts, { data: notes, loading }] = useLazyQuery(
    FETCH_CONTACTS_FOR_TIMECARD
  );
  const [refetchContacts, setRefetchContacts] = useState('');
  const getContacts = async () => {
    await _getCompanyContacts({
      variables: {
        page: 1,
        limit: CONTACT_FETCH_LIMIT,
        searchText: '',
        statusSearch: '',
        companyId,
      },
    });
  };
  useEffect(() => {
    if (companyId) {
      getContacts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, refetchContacts]);

  // Overflow guard: the selects filter client-side over the single page fetched
  // above, so a company with more contacts than CONTACT_FETCH_LIMIT would
  // silently lose the tail — the original kan-81 bug at limit 30. If any
  // company ever exceeds the raised cap, make it loud instead of a mystery.
  useEffect(() => {
    const total = notes?.allContacts?.count ?? 0;
    if (total > CONTACT_FETCH_LIMIT) {
      console.warn(
        `[placement contacts] company ${companyId} has ${total} contacts but only ` +
          `${CONTACT_FETCH_LIMIT} were loaded — the contact selects are missing the rest. ` +
          'Raise CONTACT_FETCH_LIMIT or move these selects to server-side search.'
      );
    }
  }, [notes, companyId]);


  const createContactToggle = () => {
    setCreateContactModal(!createContactModal);
  };

  const customStyles = {
    control: (styles, provided) => ({
      ...styles,

      backgroundColor: theme === 'light' ? provided.background : '#282828',
      borderColor: theme === 'light' ? '#ced4da' : '#525252',
      color: theme === 'light' ? provided.backgroundColor : '#d9d9d9',
      fontSize: 12,
      '&:hover': {
        borderColor: theme === 'light' ? '#ced4da' : '#282828',
        color: theme === 'light' ? provided.background : '#d9d9d9',
      },
    }),
    menu: (provided) => ({
      ...provided,
      background: theme === 'light' ? provided.background : '#282828',
      color: theme === 'light' ? provided.background : '#d9d9d9',
    }),
    option: (provided) => ({
      ...provided,
      fontSize: 12,
      background: theme === 'light' ? provided.background : '#363636',
      '&:hover': {
        background: theme === 'light' ? provided.background : '#474444',
        color: theme === 'light' ? provided.background : '#d9d9d9',
      },
    }),
    container: (provided, state) => ({
      ...provided,
      fontSize: 12,
    }),
  };
  return (
    <>
      <Col lg="4" md="4" sm="12" className="displayNoneHeading">
        <Label>
          Assignment Duration (weeks) <span className="text-danger">*</span>
        </Label>
        <Controller
          control={control}
          name="assignedDuration"
          rules={{
            required: {
              value: true,
              message: 'Assignment duration is required!',
            },
            maxLength: {
              value: 2,
              message:
                "Length of Assignment duration can't be more than 2 digits",
            },
          }}
          render={({ field, fieldState }) => (
            <>
              <InputField
                {...field}
                disabled={disableAllFields}
                readOnly={readonly}
                isRequired
                inputtype="text"
                placeholder="Assignment duration"
                inputid="assignedDuration"
                errMsg={fieldState.error?.message}
                onChange={(e) => {
                  field.onChange(e);
                  setAssignedDuration(e.target.value);
                  setEndingDate(
                    moment(startDateString)
                      .add(parseInt(e?.target?.value) * 7, 'days')
                      .format('YYYY-MM-DD')
                  );
                }}
              />
            </>
          )}
        />
      </Col>

      <Col
        lg="4"
        md="4"
        sm="12"
        className={`${!readonly && 'dollar-addon-btn'}`}
      >
        <FormGroup className="agreementSelectSpacing">
          <Label>
            Less Employee Pay Rate <span className="text-danger">*</span>
          </Label>

          <InputGroup>
            <InputGroupAddon
              addonType="prepend"
              className={disableAllFields ? 'diable-form-group' : ''}
            >
              $
            </InputGroupAddon>
            <Controller
              control={control}
              name="payRate"
              rules={{
                required: 'Pay rate is required',
              }}
              render={({ field, fieldState }) => (
                <>
                  <Input
                    {...field}
                    readOnly={readonly}
                    disabled={disableAllFields}
                    placeholder={'/hour'}
                    onChange={(e) => {
                      if (e.target.value) {
                        moneyFieldHandler(
                          e?.target?.value,
                          setValue,
                          'payRate'
                        );
                      } else {
                        field.onChange(e);
                      }
                    }}
                    invalid={!!fieldState.error}
                  />
                </>
              )}
            />
          </InputGroup>

          {errors?.payRate && <FormFeedback className="d-block">{errors.payRate.message as string}</FormFeedback>}
        </FormGroup>
      </Col>
      <Col
        lg="4"
        md="4"
        sm="12"
        className={`${!readonly && 'dollar-addon-btn'}`}
      >
        <FormGroup className="agreementSelectSpacing">
          <Label>
            Company Bill Rate <span className="text-danger">*</span>
          </Label>

          <InputGroup>
            <InputGroupAddon
              addonType="prepend"
              className={disableAllFields ? 'diable-form-group' : ''}
            >
              $
            </InputGroupAddon>
            <Controller
              control={control}
              name="billRate"
              rules={{
                required: 'Bill rate is required',
              }}
              render={({ field, fieldState }) => (
                <>
                  <Input
                    {...field}
                    readOnly={readonly}
                    disabled={disableAllFields}
                    placeholder={'/hour'}
                    onChange={(e) => {
                      if (e.target.value) {
                        moneyFieldHandler(
                          e?.target?.value,
                          setValue,
                          'billRate'
                        );
                        moneyFieldHandler(
                          localStringMaker(
                            commaRemover(e?.target?.value) * 1.5
                          ),
                          setValue,
                          'overtimeBillRate'
                        );
                      } else {
                        field.onChange(e);
                      }
                    }}
                    invalid={!!fieldState.error}
                  />
                </>
              )}
            />
          </InputGroup>

          {errors?.billRate && <FormFeedback className="d-block">{errors.billRate.message as string}</FormFeedback>}
        </FormGroup>
      </Col>

      <Col lg="4" md="4" sm="12" className="">
        <FormGroup>
          <Label>
            Markup Percentage <span className="text-danger">*</span>
          </Label>

          <InputGroup>
            <InputGroupAddon
              addonType="prepend"
              className={'diable-form-group'}
            >
              %
            </InputGroupAddon>
            <Controller
              control={control}
              name="markupPercentage"
              render={({ field }) => (
                <Input
                  {...field}
                  readOnly
                  disabled={disableAllFields}
                  placeholder="(Company Bill Rate - Pay Rate) / Less Employee Pay Rate"
                  value={Number(
                    ((commaRemover(watch('billRate')) -
                      commaRemover(watch('payRate'))) /
                      commaRemover(watch('payRate'))) *
                    100
                  ).toFixed(2)}
                  type="number"
                />
              )}
            />
          </InputGroup>
        </FormGroup>
      </Col>

      <Col lg="4" md="4" sm="12" className="agreementSelectSpacing">
        <Label>
          Timecard Approver <span className="text-danger">*</span>
        </Label>
        <Controller
          rules={{
            required: {
              value: true,
              message: "Time card Approver must be selected!",
            },
          }}
          name="timeCardApprover"
          render={({ field: { onChange, name, value, ref } }) => (
            <AsyncContactController
              contactData={notes?.allContacts?.contacts?.filter((v: any) => v?.status === 'ACTIVE')
                ?.map((item) => ({
                  value: item?.id,
                  label: item?.fullName,
                }))
                ?.sort((a, b) => (a?.label ?? '').localeCompare(b?.label ?? '', undefined, { sensitivity: 'base' }))}
              loading={loading}
              companyId={companyId}
              width={"100%"}
              ownershareeFlag={true}
              placeholder={`Search Contacts of ${companyName}`}
              value={value}
              isDisabled={companyId ? false : true}
              onChange={(target: { label: string; value: string }) => {
                onChange(target);
                setError('timeCardApprover', undefined); // Clear the error message
              }}
            />
          )}
          control={control}
        />
        <small className="text-danger m-0">
          {errors?.timeCardApprover?.message as any}
        </small>
        <h6>
          <Button
            disabled={readonly || disableAllFields}
            size="sm"
            onClick={createContactToggle}
            className="bg-transparent border-0 ml-0 p-0"
            style={{ color: "#0e79b6", fontSize: "12px" }}
          >
            Add Contact
          </Button>
        </h6>
      </Col>

      <Col
        lg="4"
        md="4"
        sm="12"
        className={`${!readonly && 'dollar-addon-btn'}`}
      >
        <FormGroup className="agreementSelectSpacing">
          <Label>
            Overtime Bill Rate
            <span className="text-danger">*</span>
          </Label>

          <InputGroup>
            <InputGroupAddon
              addonType="prepend"
              className={disableAllFields ? 'diable-form-group' : ''}
            >
              $
            </InputGroupAddon>
            <Controller
              control={control}
              name="overtimeBillRate"
              rules={{
                required: 'Overtime bill rate is required',
              }}
              render={({ field, fieldState }) => (
                <>
                  <Input
                    {...field}
                    readOnly={readonly}
                    disabled={disableAllFields}
                    placeholder={'/hour'}
                    onChange={(e) => {
                      if (e.target.value) {
                        moneyFieldHandler(
                          e?.target?.value,
                          setValue,
                          'overtimeBillRate'
                        );
                      } else {
                        field.onChange(e);
                      }
                    }}
                    type="text"
                    invalid={!!fieldState.error}
                  />
                </>
              )}
            />
          </InputGroup>

          {errors?.overtimeBillRate && <FormFeedback className="d-block">{errors.overtimeBillRate.message as string}</FormFeedback>}
        </FormGroup>
      </Col>

      <CreateContactModal
        toggle={createContactToggle}
        setEditModal={setEditModal}
        open={createContactModal}
        companyId={companyId}
        setRefectContact={setRefetchContacts}
        companyName={companyName}
        preSelectContact={() => { }}
        fetchCompany={() => {
          _getCompanyContacts({
            variables: {
              page: 1,
              limit: CONTACT_FETCH_LIMIT,
              searchText: "",
              statusSearch: "",
              companyId,
            },
          });
        }}
      />
    </>
  );
}
